import React from 'react'
import { Box, Grid, Typography, Collapse } from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { formatDateLocale } from '../../../../../../../lib/utils.es6'
import useProductCurrency from '../../../../../../../hooks/useProductCurrency'
import ButtonInc from '../../../../../../../shared-ui/ButtonInc'
import SkeletonWrapper from '../../../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import CoverLinkQuotationDetail from './CoverLinkQuotationDetail'

const INITIAL_VISIBLE = 5

const styles = {
  emptyWrapper: {
    textAlign: 'center',
    padding: '40px 0',
    color: '#6b6b6b',
    fontFamily: 'Open Sans',
    fontSize: '14px'
  },
  errorWrapper: {
    textAlign: 'center',
    padding: '24px',
    color: '#721c24',
    backgroundColor: '#f8d7da',
    borderRadius: '8px',
    fontFamily: 'Open Sans',
    fontSize: '13px'
  },
  card: {
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    marginBottom: '16px',
    background: '#FFFFFF',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px'
  },
  iconWrap: {
    background: 'linear-gradient(135deg, #032a37 0%, #173E4B 100%)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    flexShrink: 0
  },
  quotationNumber: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '14px',
    color: '#000000'
  },
  quotationSub: {
    fontFamily: 'Open Sans',
    fontSize: '11px',
    color: '#A9A9A9',
    textTransform: 'uppercase',
    letterSpacing: '0.005em'
  },
  pill: {
    display: 'inline-block',
    fontSize: '10px',
    padding: '3px 12px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontFamily: 'Open Sans'
  },
  pillActive: {
    backgroundColor: '#d4edda',
    color: '#155724'
  },
  pillInactive: {
    backgroundColor: '#e2e3e5',
    color: '#383d41'
  },
  metaLabel: {
    fontFamily: 'Open Sans',
    fontSize: '11px',
    color: '#A9A9A9',
    textTransform: 'uppercase',
    marginBottom: '2px'
  },
  metaValue: {
    fontFamily: 'Lato',
    fontSize: '12px',
    fontWeight: 500,
    color: '#000000'
  },
  metaRow: {
    padding: '0 20px 16px 20px'
  },
  detailBorder: {
    borderTop: '1px solid #e5e5e5',
    padding: '16px 20px 20px 20px',
    backgroundColor: '#fafafa'
  }
}

const QuoteCard = ({ quote }) => {
  const [expanded, setExpanded] = React.useState(false)
  const currencyCode = useProductCurrency(
    (quote.quotation && quote.quotation.product_code) || quote.product_code
  )

  const premium =
    quote && typeof quote.total_premium_amount_in_cents === 'number'
      ? `${currencyCode} ${(
          quote.total_premium_amount_in_cents / 100
        ).toLocaleString()}`
      : 'N/A'

  const effectiveEndorsementDate =
    quote && quote.additional_details
      ? quote.additional_details.effective_endorsement_date
      : null

  return (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.iconWrap}>
          <NoteAddOutlinedIcon sx={{ color: '#ffffff', fontSize: '18px' }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={styles.quotationNumber}>
            {quote.quotation_number || 'N/A'}
          </Typography>
          <Typography sx={styles.quotationSub}>
            {quote.product_name || 'Quotation'}
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            ...styles.pill,
            ...(quote.active ? styles.pillActive : styles.pillInactive)
          }}
        >
          {quote.active ? 'Active' : 'Inactive'}
        </Box>
      </Box>

      <Grid container spacing={2} sx={styles.metaRow}>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Start Date</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.start_date) || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>End Date</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.end_date) || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Status</Typography>
          <Typography sx={styles.metaValue}>{quote.status || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography sx={styles.metaLabel}>Premium</Typography>
          <Typography sx={styles.metaValue}>{premium}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={styles.metaLabel}>Created At</Typography>
          <Typography sx={styles.metaValue}>
            {formatDateLocale(quote.created_at) || 'N/A'}
          </Typography>
        </Grid>
        {effectiveEndorsementDate && (
          <Grid item xs={12} sm={6}>
            <Typography sx={styles.metaLabel}>
              Effective Endorsement Date
            </Typography>
            <Typography sx={styles.metaValue}>
              {formatDateLocale(effectiveEndorsementDate) || 'N/A'}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sx={{ textAlign: 'right' }}>
          <ButtonInc
            btnType="secondary"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {expanded ? 'Hide details' : 'View details'}
          </ButtonInc>
        </Grid>
      </Grid>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={styles.detailBorder}>
          <CoverLinkQuotationDetail quotation={quote} meta={quote} />
        </Box>
      </Collapse>
    </Box>
  )
}

const CoverLinkQuotesList = props => {
  const [showAll, setShowAll] = React.useState(false)

  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  if (props.error) {
    return (
      <Box sx={styles.errorWrapper}>
        Unable to load quotations. Please try again later.
      </Box>
    )
  }

  const quotes = Array.isArray(props.quotes) ? props.quotes : []

  if (quotes.length === 0) {
    return <Box sx={styles.emptyWrapper}>No quotations found</Box>
  }

  const sorted = quotes
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE)

  return (
    <Box>
      {visible.map((quote, index) => (
        <QuoteCard key={quote.guid || index} quote={quote} />
      ))}
      {sorted.length > INITIAL_VISIBLE && (
        <Box sx={{ textAlign: 'center', marginTop: '8px' }}>
          <ButtonInc
            btnType="secondary"
            size="small"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show less' : `Show all ${sorted.length} quotations`}
          </ButtonInc>
        </Box>
      )}
    </Box>
  )
}

export default CoverLinkQuotesList
